/**
 * Centralized copy for the Member › MLD tab inside Customer Information
 * (native Member List Deal, mock Option C). The words for MLD codes, chips,
 * dates and the lifecycle stepper are shared with the list and the deal
 * detail, so they live in `pages/mld/strings.ts`; this file holds only what is
 * specific to the tab. Placeholders use `{token}` and are filled with the same
 * `formatMessage` helper as the Book Access tab.
 *
 * Nothing here repeats the Subscriptions tab (no raw Chargebee status, no
 * "Current period", no Chargebee links): those stay on that tab and on the
 * deal detail page.
 */
export { formatMessage } from './MemberBooksTab.strings';

export const memberMldStrings = {
  tabTitle: 'MLD',
  heading: 'Member List Deals',
  loading: "Loading this member's deals…",
  loadError: "Could not load this member's deals. Please try again.",
  retry: 'Try again',
  empty: 'This member has no Member List Deal yet.',
  card: {
    price: 'Price',
    subscriptionType: 'Subscription type',
    stepperLabel: 'Deal lifecycle',
    /**
     * The visually hidden state after a stage's label, so a screen reader
     * reads "Trial, Not reached" rather than "TrialNot reached". The words
     * are `mldStrings.stepper.state`.
     */
    stageState: ', {state}',
    openDeal: 'Open deal',
    /** The deal's last payment: "$322.92 · Oct 8 · Automatic" (ADR-007 decision 10). */
    lastPayment: 'Last payment',
  },
  ledger: {
    heading: 'Payments and status changes',
    empty: 'Nothing recorded for this filter.',
    filters: {
      all: 'All',
      sales: 'Sales',
      failed: 'Failed',
      statusChanges: 'Status changes',
    },
    filterLabel: 'Show',
    /**
     * Names the deal a merged ledger row belongs to and what the row was for:
     * "AFY · Authorify Premium" (a payment's own plan, else the deal's plan)
     * or "AFY · 52 printed book credits (upsell offer)" (a one-time charge).
     * The card title uses it with the deal's current plan.
     */
    deal: '{family} · {item}',
    columns: {
      when: 'When',
      deal: 'Deal',
      what: 'What happened',
      who: 'Who',
    },
    /** Joins the parts of a payment row: "Payment · $49.00 · Billed-through". */
    separator: ' · ',
  },
} as const;
